<template>
  <div
    :class="[
      'pagination-container',
      noPaddingClass,
      reverseOrderClass,
      'tw-mt-auto tw-grid tw-grid-cols-3 tw-items-center tw-p-3.5 tw-text-sm tw-font-normal tw-text-neutral-400 md:tw-flex md:tw-grid-cols-1 md:tw-flex-col md:tw-justify-center md:tw-gap-4',
    ]"
  >
    <div class="items-per-page">
      <template v-if="!props.hideTextPerPage">
        <span>{{ props.textPerPage }}</span>
        <FormKit
          id="items-per-page"
          type="select"
          v-model="itemsPerPage"
          :options="optionsPerPage"
          :disabled="isNoItem"
        />
      </template>
    </div>
    <nav aria-label="Page navigation" id="page-navigation">
      <MDBPagination class="page-navigation-container" tag="ul">
        <MDBPageItem :disabled="isFirstPage || isNoItem" @click.prevent="firstPage()" v-if="!props.minimalPageNumber">
          <span class="SproutIcon SproutIcon-backward-solid"></span>
        </MDBPageItem>
        <MDBPageItem :disabled="isFirstPage || isNoItem" @click.prevent="prevPage()">
          <span class="SproutIcon SproutIcon-caret-left-solid-1"></span>
        </MDBPageItem>
        <span class="page-item input-page">
          <span v-if="!props.minimalPageNumber">Page</span>
          <input
            type="number"
            id="current-page-number"
            v-model="currentPage"
            @input.prevent="jumpToPage($event)"
            :disabled="isNoItem"
            :class="{ minimal: props.minimalPageNumber }"
            ref="pageInputref"
          />
          of
          {{ totalPages }}
        </span>
        <MDBPageItem
          :disabled="isLastPage || isNoItem"
          @click.prevent="nextPage()"
          data-testid="spa-pagination-next-page"
        >
          <span class="SproutIcon SproutIcon-caret-right-solid-1"></span>
        </MDBPageItem>
        <MDBPageItem :disabled="isLastPage || isNoItem" @click.prevent="lastPage()" v-if="!props.minimalPageNumber">
          <span class="SproutIcon SproutIcon-forward-solid"></span>
        </MDBPageItem>
      </MDBPagination>
    </nav>
    <div class="items-range">
      <span v-if="!props.minimalPageNumber">Showing&nbsp;</span>
      {{ showItemsFrom }} - {{ showItemsTo }} of {{ totalItems }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useDebounceFn, useEventListener } from '@vueuse/core';

import { MDBPagination, MDBPageItem } from 'mdb-vue-ui-kit';

const props = defineProps({
  pagination: {
    currentPage: Number,
    itemsPerPage: Number,
    totalItems: Number,
    totalPages: Number,
  },
  textPerPage: {
    type: String,
    default: 'Items per Page',
  },
  hideTextPerPage: {
    type: Boolean,
    default: false,
  },
  minimalItemRange: {
    type: Boolean,
    default: false,
  },
  minimalPageNumber: {
    type: Boolean,
    default: false,
  },
  reverseOrder: {
    type: Boolean,
    default: false,
  },
  noPadding: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['changePage']);

const currentPage = ref(props.pagination.currentPage);
const itemsPerPage = ref(props.pagination.itemsPerPage);
const totalItems = ref(props.pagination.totalItems);
const totalPages = ref(props.pagination.totalPages);
const noPaddingClass = computed(() => (props.noPadding ? 'no-padding' : ''));
const reverseOrderClass = computed(() => (props.reverseOrder ? 'reverse-order' : ''));

watch(
  () => props.pagination,
  (newValue) => {
    currentPage.value = newValue.currentPage;
    itemsPerPage.value = newValue.itemsPerPage;
    totalItems.value = newValue.totalItems;
    totalPages.value = newValue.totalPages;
  },
  { immediate: true },
);

const optionsPerPage = ref([
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
]);
const itemsFrom = computed(() => {
  const page = currentPage.value != props.pagination.currentPage ? props.pagination.currentPage : currentPage.value;
  return (page - 1) * itemsPerPage.value;
});
const itemsTo = computed(() => itemsFrom.value + itemsPerPage.value);
const showItemsFrom = computed(() => {
  if (itemsFrom.value < 0) return 0;
  if (totalItems.value > 0) {
    if (itemsFrom.value > totalItems.value) {
      return 1;
    }
    return itemsFrom.value + 1;
  }
  return 0;
});

const showItemsTo = computed(() => {
  if (itemsTo.value < 0) return 0;
  if (totalItems.value > 0) {
    if (itemsTo.value > totalItems.value) {
      return totalItems.value;
    }
    return itemsTo.value;
  }
  return 0;
});

const computeNextPage = computed(() => {
  if (currentPage.value < totalPages.value) {
    return parseInt(currentPage.value) + 1;
  }
  return currentPage.value;
});

const computePrevPage = computed(() => {
  if (currentPage.value != 1) {
    return currentPage.value - 1;
  }
  return currentPage.value;
});

const isFirstPage = computed(() => currentPage.value === 1);
const isLastPage = computed(() => currentPage.value === totalPages.value);
const isNoItem = computed(() => totalItems.value === 0);

const nextPage = () => {
  if (isLastPage.value || isNoItem.value) return;
  changeCurrentPage(computeNextPage.value);
};

const prevPage = () => {
  if (isFirstPage.value || isNoItem.value) return;
  changeCurrentPage(computePrevPage.value);
};

const firstPage = () => {
  if (isFirstPage.value || isNoItem.value) return;
  changeCurrentPage(1);
};

const lastPage = () => {
  if (isLastPage.value || isNoItem.value) return;
  changeCurrentPage(totalPages.value);
};

const jumpToPage = useDebounceFn((e) => {
  e.preventDefault();
  const pageValue = e.target.value;
  const pageValueInt = parseInt(pageValue);
  const pageValueData = e.data;
  const pageValidatorString = /^[1-9]\d*$/;
  if (
    pageValueInt <= 0 ||
    pageValueInt > totalPages.value ||
    (!pageValidatorString.test(pageValue) &&
      (pageValue === '' ||
        pageValueData === '-' ||
        pageValueData === 'e' ||
        pageValueData === '.' ||
        pageValueData === '+'))
  ) {
    changeCurrentPage(1);
  } else {
    changeCurrentPage(pageValue);
  }
}, 1000);

const changeCurrentPage = (newCurrentPage) => {
  const paginate = {
    currentPage: newCurrentPage,
    itemsPerPage: itemsPerPage.value,
  };
  emit('changePage', paginate);
};

const changeItemPerPage = (newItemPerPage) => {
  const paginate = { currentPage: 1, itemsPerPage: newItemPerPage };
  emit('changePage', paginate);
};

watch([itemsPerPage], ([newItemsPerPage]) => {
  changeItemPerPage(newItemsPerPage);
});

const pageInputref = ref();
useEventListener(
  pageInputref,
  'wheel',
  (event) => {
    event.target.blur();
  },
  { passive: true },
);
useEventListener(pageInputref, 'keydown', (event) => {
  const inputVal = event.target.value;

  if (
    (event.key === '-' && inputVal.indexOf('-') !== 0) ||
    (/\d/.test(event.key) && inputVal.indexOf('-') > 0) ||
    (event.key === 'ArrowDown' && parseInt(inputVal) <= 1)
  ) {
    event.preventDefault();
  }
});
</script>

<style lang="scss" scoped>
.pagination-container {
  &.no-padding {
    padding: 0;
  }

  &.reverse-order {
    display: flex;
    flex-direction: row-reverse;
    .items-range {
      margin-right: auto;
    }
  }

  .items-range {
    justify-content: end;
    display: flex;
    align-items: center;
    column-gap: 4px;
  }

  .items-per-page {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    white-space: nowrap;

    & :deep(.formkit-outer) {
      margin: 0;
      & .formkit-inner:focus-within {
        box-shadow: none;
        border: 1px solid $green-300;
      }

      & select.formkit-input {
        height: 28px;
        padding: 6px 10px;
        min-width: 60px;
      }
    }

    & span {
      margin-right: 8px;
    }
  }

  .page-navigation-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 0;
    white-space: nowrap;

    .page-item {
      :deep(.page-link) {
        color: $green-300;
        font-size: 14px;
        line-height: 24px;
        padding-inline: 8px;
        cursor: pointer;

        .SproutIcon {
          vertical-align: middle;
        }
      }

      &.input-page {
        color: $neutral-400;
        padding-inline: 8px;
        font-size: 14px;
        line-height: 24px;

        #current-page-number {
          margin: 4px 8px;
          width: 48px;
          border-radius: 4px;
          border: 1px solid $neutral-60;
          text-align: center;

          &.minimal {
            width: 32px;
          }
        }

        #current-page-number::-webkit-outer-spin-button,
        #current-page-number::-webkit-inner-spin-button {
          appearance: none;
          -webkit-appearance: none;
          margin: 0;
        }

        #current-page-number[type='number'] {
          appearance: textfield;
          -moz-appearance: textfield;
        }
      }
    }

    :deep(.page-item.disabled .page-link) {
      color: $neutral-40;
      pointer-events: none;
    }
  }

  .select-option:hover:not(.disabled) {
    background-color: $green-75;
  }

  .select-option.active,
  .select-option.selected.active {
    background-color: $green-50;
  }

  :deep(.form-outline) {
    margin-bottom: 0;
    width: 65px;
  }

  :deep(.select-wrapper .form-outline input.form-control) {
    max-height: 24px;
    padding-top: 0.33em;
    padding-bottom: 0.33em;
    padding-left: 0.75em;
    padding-right: 0.75em;
    max-width: 80px;
    line-height: 1.6;
  }

  :deep(.select-wrapper .form-outline .select-arrow) {
    top: 2px;
  }
}
</style>
